import { Camera, ImagePlus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PhotoCropModal from "./PhotoCropModal";
import "./NegativeStripOfferEditor.css";

const STRIP_IMAGE_SRC =
  "/images/frame-gallery/frame-gallery-tira-negativos/frame-gallery-tira-negativos-editor.png";

const CANVAS_SIZE = {
  w: 1000,
  h: 436,
};

const PHOTO_BOXES = [
  { x: 60, y: 85, w: 275, h: 270 },
  { x: 365, y: 85, w: 275, h: 270 },
  { x: 670, y: 85, w: 275, h: 270 },
];

function createEmptySlots() {
  return PHOTO_BOXES.map(() => null);
}

function getBoxStyle(box) {
  return {
    left: `${(box.x / CANVAS_SIZE.w) * 100}%`,
    top: `${(box.y / CANVAS_SIZE.h) * 100}%`,
    width: `${(box.w / CANVAS_SIZE.w) * 100}%`,
    height: `${(box.h / CANVAS_SIZE.h) * 100}%`,
  };
}

export default function NegativeStripOfferEditor({
  title = "Elige tus 3 momentos",
  description = "Sube tres fotos y ajusta el encuadre directamente sobre la tira.",
  submitLabel = "Dejar fotos listas",
  onPhotosChange,
  onSubmit,
}) {
  const inputRef = useRef(null);
  const activeSlotRef = useRef(0);

  const [slots, setSlots] = useState(createEmptySlots);
  const [activeSlot, setActiveSlot] = useState(0);
  const [cropPhoto, setCropPhoto] = useState(null);
  const [message, setMessage] = useState("");

  const completedCount = useMemo(() => slots.filter(Boolean).length, [slots]);
  const photos = useMemo(() => slots.filter(Boolean), [slots]);

  useEffect(() => {
    onPhotosChange?.(photos);
  }, [onPhotosChange, photos]);

  useEffect(() => {
    if (!message) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setMessage("");
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [message]);

  const openFileSelector = (slotIndex) => {
    activeSlotRef.current = slotIndex;
    setActiveSlot(slotIndex);
    inputRef.current?.click();
  };

  const startCrop = (file, slotIndex) => {
    if (!file?.type?.startsWith("image/")) {
      setMessage("Selecciona una imagen válida.");
      return;
    }

    const preview = URL.createObjectURL(file);

    setMessage("");
    setActiveSlot(slotIndex);
    activeSlotRef.current = slotIndex;
    setCropPhoto({
      file,
      name: file.name,
      preview,
      slotIndex,
      isStoredPreview: false,
    });
  };

  const closeCrop = () => {
    if (cropPhoto?.preview && !cropPhoto.isStoredPreview) {
      URL.revokeObjectURL(cropPhoto.preview);
    }

    setCropPhoto(null);
  };

  const confirmCrop = (file) => {
    const slotIndex = cropPhoto?.slotIndex ?? activeSlot;
    const sourcePreview = cropPhoto?.preview;
    const objectUrl = URL.createObjectURL(file);

    setSlots((currentSlots) => {
      const nextSlots = [...currentSlots];
      const oldSlot = nextSlots[slotIndex];

      if (oldSlot?.objectUrl) {
        URL.revokeObjectURL(oldSlot.objectUrl);
      }

      if (
        oldSlot?.sourcePreview &&
        oldSlot.sourcePreview !== sourcePreview &&
        oldSlot.sourcePreview !== oldSlot.objectUrl
      ) {
        URL.revokeObjectURL(oldSlot.sourcePreview);
      }

      nextSlots[slotIndex] = {
        file,
        objectUrl,
        sourcePreview,
        name: file.name || `foto-${slotIndex + 1}.jpg`,
      };

      return nextSlots;
    });

    setMessage("");
    setCropPhoto(null);
  };

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (file) {
      startCrop(file, activeSlotRef.current);
    }
  };

  const clearPhotos = () => {
    slots.forEach((slot) => {
      if (slot?.objectUrl) URL.revokeObjectURL(slot.objectUrl);
      if (slot?.sourcePreview && slot.sourcePreview !== slot.objectUrl) {
        URL.revokeObjectURL(slot.sourcePreview);
      }
    });

    setSlots(createEmptySlots());
    setCropPhoto(null);
    setMessage("");
  };

  const openCropForSlot = (slot, slotIndex) => {
    if (!slot) return;

    setCropPhoto({
      file: slot.file,
      name: slot.name,
      preview: slot.sourcePreview || slot.objectUrl,
      slotIndex,
      isStoredPreview: true,
    });
  };

  const handleSubmit = () => {
    if (completedCount < PHOTO_BOXES.length) {
      const pending = PHOTO_BOXES.length - completedCount;
      setMessage(
        `Falta ${pending} foto${pending === 1 ? "" : "s"} para completar el pack.`
      );
      return;
    }

    onSubmit?.(photos);
  };

  return (
    <section className="negative-strip-editor" aria-labelledby="negative-strip-title">
      <div className="negative-strip-editor__heading">
        <p>Personaliza la oferta</p>
        <h2 id="negative-strip-title">{title}</h2>
        <span>{description}</span>
      </div>

      <div className="negative-strip-editor__canvas-wrap">
        <img
          className="negative-strip-editor__strip"
          src={STRIP_IMAGE_SRC}
          alt="Tira de negativos personalizada"
          draggable="false"
        />

        {PHOTO_BOXES.map((box, index) => {
          const slot = slots[index];
          const boxStyle = getBoxStyle(box);

          return (
            <div key={index} className="negative-strip-editor__box" style={boxStyle}>
              {slot && (
                <img
                  className="negative-strip-editor__photo"
                  src={slot.objectUrl}
                  alt={`Foto ${index + 1}`}
                  draggable="false"
                  role="button"
                  tabIndex={0}
                  onClick={() => openCropForSlot(slot, index)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openCropForSlot(slot, index);
                    }
                  }}
                />
              )}

              <button
                type="button"
                className={
                  slot
                    ? "negative-strip-editor__slot is-filled"
                    : "negative-strip-editor__slot"
                }
                onClick={() => {
                  if (slot) {
                    openCropForSlot(slot, index);
                  } else {
                    openFileSelector(index);
                  }
                }}
                aria-label={slot ? `Editar foto ${index + 1}` : `Subir foto ${index + 1}`}
              >
                {slot ? (
                  <Camera aria-hidden="true" />
                ) : (
                  <span>
                    <ImagePlus aria-hidden="true" />
                    <strong>Foto {index + 1}</strong>
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleInputChange}
      />

      <div className="negative-strip-editor__summary">
        <strong>
          {completedCount} / {PHOTO_BOXES.length} fotos
        </strong>
        <span>Toca una foto cargada para ajustar el encuadre.</span>
      </div>

      {message && <p className="negative-strip-editor__message">{message}</p>}

      <div className="negative-strip-editor__actions">
        <button type="button" onClick={clearPhotos}>
          <Trash2 aria-hidden="true" /> Borrar fotos
        </button>
        <button type="button" onClick={handleSubmit}>
          {submitLabel}
        </button>
      </div>

      {cropPhoto &&
        createPortal(
          <div className="negative-strip-editor negative-strip-editor--modal-root">
            <PhotoCropModal
              image={cropPhoto}
              onClose={closeCrop}
              onConfirm={confirmCrop}
              onChangeImage={() => openFileSelector(cropPhoto.slotIndex)}
            />
          </div>,
          document.body,
        )}
    </section>
  );
}